import { useEffect, useRef } from 'react';
import { Box } from '@chakra-ui/react';
import { useQuadViewStore, type QuadViewportId } from '../../state/useQuadViewStore';
import { useViewportFocus } from '../../state/useViewportFocus';

/**
 * QuadLayout — the 3D workbench's 2x2 viewport shell.
 *
 * Replaces the fixed CSS grid with a pointer-resizable split: the vertical
 * and horizontal splitters move one axis each, and the center handle where
 * all four cells meet moves both at once (drag in any direction). Ratios
 * live in useQuadViewStore so they survive workbench switches.
 *
 * Alt+W (registered as the 'toggleMaximizeViewport' command, dispatched as
 * the 'yap:toggle-maximize-viewport' chord) maximizes the viewport that
 * last received a pointer (useViewportFocus; defaults to the main
 * perspective cell) and toggles it back to the 2x2 layout.
 */

/** Cell gap in px (the splitter band the container background shows). */
const GAP = 4;
/** Ratio clamp bounds — no cell may collapse fully. */
const MIN_RATIO = 0.15;
const MAX_RATIO = 0.85;

const clampRatio = (value: number) =>
    Math.min(MAX_RATIO, Math.max(MIN_RATIO, value));

export interface QuadLayoutCells {
    top: React.ReactNode;
    front: React.ReactNode;
    right: React.ReactNode;
    main: React.ReactNode;
}

/** Render order of the four cells: top-left, top-right, bottom-left,
    bottom-right (main). */
const QUAD_CELL_IDS = ['top', 'front', 'right', 'main'] as const;
type QuadCellKey = (typeof QUAD_CELL_IDS)[number];

interface QuadLayoutProps {
    cells: QuadLayoutCells;
}

/** Absolutely-positioned geometry of one cell from the current ratios. */
function cellStyle(
    id: QuadViewportId,
    columnRatio: number,
    rowRatio: number
): React.CSSProperties {
    const half = GAP / 2;
    const leftColumn = id === 'top' || id === 'right';
    const topRow = id === 'top' || id === 'front';
    return {
        position: 'absolute',
        overflow: 'hidden',
        left: leftColumn ? 0 : `calc(${columnRatio * 100}% + ${half}px)`,
        top: topRow ? 0 : `calc(${rowRatio * 100}% + ${half}px)`,
        width: `calc(${(leftColumn ? columnRatio : 1 - columnRatio) * 100}% - ${half}px)`,
        height: `calc(${(topRow ? rowRatio : 1 - rowRatio) * 100}% - ${half}px)`,
    };
}

type DragMode = 'column' | 'row' | 'both';

export function QuadLayout({ cells }: QuadLayoutProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const columnRatio = useQuadViewStore((s) => s.layout.columnRatio);
    const rowRatio = useQuadViewStore((s) => s.layout.rowRatio);
    const setLayout = useQuadViewStore((s) => s.setLayout);
    const maximized = useQuadViewStore((s) => s.maximized);
    const setFocus = useViewportFocus((s) => s.setFocus);

    /**
     * Shared drag driver for all three handles: captures the pointer,
     * converts pointer motion into clamped ratios and writes them straight
     * to the store so the cells resize live.
     */
    const startDrag = (mode: DragMode) => (event: React.PointerEvent) => {
        event.preventDefault();
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;
        const dragCursor = mode === 'column' ? 'col-resize' : mode === 'row' ? 'row-resize' : 'move';

        const move = (e: PointerEvent) => {
            const patch: { columnRatio?: number; rowRatio?: number } = {};
            if (mode !== 'row') {
                patch.columnRatio = clampRatio((e.clientX - rect.left) / rect.width);
            }
            if (mode !== 'column') {
                patch.rowRatio = clampRatio((e.clientY - rect.top) / rect.height);
            }
            setLayout(patch);
        };
        const up = () => {
            document.removeEventListener('pointermove', move);
            document.removeEventListener('pointerup', up);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
        document.addEventListener('pointermove', move);
        document.addEventListener('pointerup', up);
        document.body.style.cursor = dragCursor;
        document.body.style.userSelect = 'none';
    };

    // Alt+W chord: maximize the focused viewport, or restore the quad when
    // one is already maximized. Focus ids outside the quad shell ('main' is
    // the default) resolve to the perspective cell.
    useEffect(() => {
        const toggle = () => {
            const quad = useQuadViewStore.getState();
            if (quad.maximized) {
                quad.setMaximized(null);
                return;
            }
            const focus = useViewportFocus.getState().focus;
            const active: QuadViewportId =
                focus === 'top' || focus === 'front' || focus === 'right' ? focus : 'main';
            quad.setMaximized(active);
        };
        window.addEventListener('yap:toggle-maximize-viewport', toggle);
        return () => window.removeEventListener('yap:toggle-maximize-viewport', toggle);
    }, []);

    // Leaving the 3D workbench restores the shared shell state so returning
    // reopens the full 2x2 layout with the perspective cell focused.
    useEffect(() => () => {
        useQuadViewStore.getState().setMaximized(null);
        useViewportFocus.getState().setFocus('main');
    }, []);

    // Maximized: the active viewport takes the whole quad area alone.
    if (maximized) {
        const node =
            maximized === 'main' ? cells.main
                : maximized === 'top' ? cells.top
                    : maximized === 'front' ? cells.front
                        : cells.right;
        return (
            <Box
                ref={containerRef}
                flex="1"
                minH="0"
                position="relative"
                onPointerDownCapture={() => setFocus(maximized)}
            >
                <Box w="100%" h="100%" position="relative" overflow="hidden" borderRadius="sm">
                    {node}
                </Box>
            </Box>
        );
    }

    const splitterBase: React.CSSProperties = {
        position: 'absolute',
        zIndex: 2,
    };

    return (
        <Box ref={containerRef} flex="1" minH="0" position="relative" bg="border.base">
            {QUAD_CELL_IDS.map((id) => (
                <Box
                    key={id}
                    borderRadius="sm"
                    onPointerDownCapture={() => setFocus(id)}
                    style={cellStyle(id, columnRatio, rowRatio)}
                >
                    {cells[id]}
                </Box>
            ))}

            {/* Vertical splitter: drags the column split left/right */}
            <Box
                title="Drag to resize columns"
                bg="transparent"
                _hover={{ bg: 'blueAlpha.500' }}
                _active={{ bg: 'blue.400' }}
                onPointerDown={startDrag('column')}
                style={{
                    ...splitterBase,
                    left: `calc(${columnRatio * 100}% - ${GAP / 2 + 4}px)`,
                    top: 0,
                    bottom: 0,
                    width: GAP + 8,
                    cursor: 'col-resize',
                }}
                role="separator"
                aria-orientation="vertical"
            />
            {/* Horizontal splitter: drags the row split up/down */}
            <Box
                title="Drag to resize rows"
                bg="transparent"
                _hover={{ bg: 'blueAlpha.500' }}
                _active={{ bg: 'blue.400' }}
                onPointerDown={startDrag('row')}
                style={{
                    ...splitterBase,
                    top: `calc(${rowRatio * 100}% - ${GAP / 2 + 4}px)`,
                    left: 0,
                    right: 0,
                    height: GAP + 8,
                    cursor: 'row-resize',
                }}
                role="separator"
                aria-orientation="horizontal"
            />
            {/* Center handle: where all four cells meet — drags both splits
                at once in any direction. */}
            <Box
                title="Drag to resize all viewports"
                bg="transparent"
                borderRadius="sm"
                _hover={{ bg: 'blue.400' }}
                _active={{ bg: 'blue.400' }}
                onPointerDown={startDrag('both')}
                style={{
                    position: 'absolute',
                    left: `calc(${columnRatio * 100}% - 8px)`,
                    top: `calc(${rowRatio * 100}% - 8px)`,
                    width: 16,
                    height: 16,
                    cursor: 'move',
                    zIndex: 3,
                }}
                role="separator"
            />
        </Box>
    );
}
